import { Shape, ShapeOptions } from "../Shape";
import { SVGParsedPathData } from "../../../svg/";
import { YZCanvasContext } from "../canvas";
import {
  angle,
  distance,
  polar,
  toDegree,
  toRadian
} from "@zyzgroup/core-common";

export type LineShapeOptions = {
  lineLength?: number;
  // 渐变色
  startColor?: string;
  endColor?: string;
} & ShapeOptions;

/**
 * canvas svg Vector 坐标系
 *                [-90° / 270°]
 *                  *
 *                  *
 *                  *
 *  [180°] *  *  *  *  *  *  *  [0°]
 *                  *
 *                  *
 *                  *
 *                [90°]
 */

// 直线：默认以 （0,0）为起点， 0° 划线
export class LineShape extends Shape {
  public lineLength: number;
  public startColor?: string;
  public endColor?: string;

  constructor(options?: LineShapeOptions) {
    // transformOrigin 为（0,0）
    super({ ...options, centerX: 0, centerY: 0 });
    this.lineLength = options?.lineLength || 0;
    this.startColor = options?.startColor;
    this.endColor = options?.endColor;
    this.setAttr({
      strokeWidth: this.getAttr("strokeWidth") || "1",
      stroke: this.startColor || this.getAttr("stroke") || "#000000"
    });
  }

  protected cloneShape(): LineShape {
    const shape = new LineShape({
      ...super.getBaseOptions(),
      lineLength: this.lineLength,
      startColor: this.startColor,
      endColor: this.endColor
    });
    return shape;
  }

  // 供外部变更
  public lineToAbsolutePoint(point: { x: number; y: number }) {
    const degree = toDegree(angle(this.x, this.y, point.x, point.y));
    const lineLength = distance(this.x, this.y, point.x, point.y);
    if (degree != this.degree || lineLength != this.lineLength) {
      if (degree != this.degree) {
        this.rotateTo(degree);
      }
      if (lineLength != this.lineLength) {
        this.lineLength = lineLength;
      }
      this.shouldUpdatePathD = true;
    }
  }

  public enableGradient(startColor: string, endColor: string) {
    this.startColor = startColor;
    this.endColor = endColor;
    this.setAttr({
      stroke: this.startColor
    });
    // 在 toCanvasContext 或 toSVGPathData 里设置渐变
  }
  public disableGradient(startColor: string) {
    this.startColor = startColor;
    this.endColor = undefined;
    this.setAttr({
      stroke: this.startColor
    });
  }

  public getBoundingClientRect() {
    return {
      x: 0,
      y: 0,
      width: this.lineLength,
      height: parseFloat(this.getAttr("strokeWidth") as string)
    };
  }

  public toSVGPathData(): SVGParsedPathData[] {
    return [
      ["m", 0, 0],
      ["l", this.lineLength, 0]
    ];
  }

  public toCanvasContext(ctx: YZCanvasContext): void {
    if (this.startColor && this.endColor) {
      // canvas 支持渐变色
      this.setAttr({
        stroke: ctx.createLinearGradient(0, 0, this.lineLength, 0, {
          0: this.startColor,
          1: this.endColor
        })
      });
    }
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(this.lineLength, 0);
  }

  // 绝对值 - 考虑 transform : translate rotate scale
  public intersection(line: LineShape): { x: number; y: number } | null {
    const l1 = this.transformPathD().normalizedLines[0];
    const l2 = line.transformPathD().normalizedLines[0];
    return l1.intersection(l2);
  }
}

export default LineShape;
